import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Card,
  IncludeTestToggle,
  useIncludeTest,
  TestDataBanner,
} from "./adminUi";

const FILTERS = [
  ["", "All"],
  ["free", "Free"],
  ["trial", "Trial"],
  ["paid", "Paid"],
  ["gift", "Gift"],
  ["sponsor", "Sponsor-attributed"],
];

const SOURCES = [
  ["", "Any source"],
  ["i_need_a_proverb", "I Need a Proverb"],
  ["lifeguide", "LifeGuide direct"],
];

export default function AdminUsersPage() {
  const [filter, setFilter] = useState("");
  const [sourceProduct, setSourceProduct] = useState("");
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [data, setData] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRows([]);
    setCursor(null);
    load(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sourceProduct, includeTest]);

  const load = async (nextCursor, reset = false) => {
    setLoading(true);
    try {
      const res = await adminApi.users({
        limit: 50,
        ...(filter ? { filter } : {}),
        ...(sourceProduct ? { source_product: sourceProduct } : {}),
        ...(includeTest ? { include_test: 1 } : {}),
        ...(nextCursor ? { cursor: nextCursor } : {}),
      });
      setData(res);
      setRows((prev) => (reset ? res.rows : [...prev, ...res.rows]));
      setCursor(res.next_cursor);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const membership = (u) => {
    if (u.stripe_subscription_status === "active" || u.stripe_subscription_status === "trialing")
      return "paid";
    if (u.access_source === "trial") return "trial";
    if (u.access_source === "gift") return "gift";
    if (u.access_source === "personal_subscription") return "paid";
    return "free";
  };

  return (
    <div data-testid="admin-users-page" className="space-y-6">
      <PageHeader
        eyebrow="Directory"
        title="Users"
        subtitle="Account-level directory for master administration. Private content (guidance, journal, wisdom, reflections) is never shown here."
        right={<IncludeTestToggle value={includeTest} onChange={setIncludeTest} />}
      />

      <TestDataBanner show={includeTest} />

      <Card>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-xs text-[#A9BFA9] inline-flex items-center gap-2">
            Membership
            <select
              data-testid="users-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-[#0F1512] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white"
            >
              {FILTERS.map(([v, l]) => (
                <option key={v || "all"} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-[#A9BFA9] inline-flex items-center gap-2">
            Source
            <select
              data-testid="users-source"
              value={sourceProduct}
              onChange={(e) => setSourceProduct(e.target.value)}
              className="bg-[#0F1512] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white"
            >
              {SOURCES.map(([v, l]) => (
                <option key={v || "any"} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <div className="ml-auto text-xs text-[#8FA894]" data-testid="users-total">
            {data?.total ?? "—"} matching users
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[#8FA894] text-[10px] uppercase tracking-[0.14em]">
            <tr>
              <th className="text-left px-3 py-3">Email</th>
              <th className="text-left px-3 py-3">Signup</th>
              <th className="text-left px-3 py-3">Source</th>
              <th className="text-left px-3 py-3">Sponsor</th>
              <th className="text-left px-3 py-3">Membership</th>
              <th className="text-left px-3 py-3">Trial started</th>
              <th className="text-left px-3 py-3">Access expires</th>
              <th className="text-left px-3 py-3">Last active</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-[#8FA894]" data-testid="users-empty">
                  No users match these filters.
                </td>
              </tr>
            )}
            {rows.map((u) => (
              <tr
                key={u.id}
                data-testid={`users-row-${u.id}`}
                className="border-t border-white/5 hover:bg-white/[0.04]"
              >
                <td className="px-3 py-2 text-white">
                  <div className="flex flex-col">
                    <span>{u.email || "—"}</span>
                    <span className="text-[10px] text-[#607467]">{u.id.slice(0, 8)}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-[#B7D2AF]">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-[#B7D2AF]">{u.source_product || "lifeguide"}</td>
                <td className="px-3 py-2 text-[#B7D2AF]">{u.sponsor_id ? u.sponsor_id.slice(0, 8) : "—"}</td>
                <td className="px-3 py-2">
                  <span
                    className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                      membership(u) === "paid"
                        ? "bg-[#4F6F52]/25 text-[#B7D2AF] border-[#4F6F52]/50"
                        : membership(u) === "trial"
                        ? "bg-[#B4795C]/20 text-[#F3D6A6] border-[#B4795C]/40"
                        : membership(u) === "gift"
                        ? "bg-[#6B7BAF]/20 text-[#C9D2F3] border-[#6B7BAF]/40"
                        : "bg-white/5 text-[#8FA894] border-white/10"
                    }`}
                  >
                    {membership(u)}
                  </span>
                </td>
                <td className="px-3 py-2 text-[#B7D2AF]">
                  {u.trial_started_at ? u.trial_started_at.slice(0, 10) : "—"}
                </td>
                <td className="px-3 py-2 text-[#B7D2AF]">
                  {u.access_expires_at ? u.access_expires_at.slice(0, 10) : "—"}
                </td>
                <td className="px-3 py-2 text-[#B7D2AF]">
                  {u.last_active_at ? u.last_active_at.slice(0, 10) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="flex justify-center">
        {cursor && (
          <button
            data-testid="users-load-more"
            onClick={() => load(cursor)}
            disabled={loading}
            className="text-xs text-[#B7D2AF] hover:text-white bg-white/5 border border-white/10 rounded-full px-4 py-1.5"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}
